import React from "react";
import { Link, useParams } from "react-router-dom";
import ArticlePage from "./ArticlePage";
import { LEARN_TOPICS, articleFromTopic } from "../content/learn";
import { useSeo } from "../lib/useSeo";
import { PublicPageHeader, PublicPageFooter } from "./PublicPageChrome";

// LearnTopicPage — renders one entry from LEARN_TOPICS by :propertyType.
//
// Two responsibilities the plain ArticlePage doesn't cover:
//   1. Unknown slugs render a real 404-shaped page (noindex, links back to
//      the /learn hub) rather than the app's catch-all Dashboard mount at
//      /*, which would confuse crawlers about which URLs are canonical.
//   2. Draft topics surface a visible banner on the page so a reviewer can't
//      miss that they're looking at unfinished content. The SEO title still
//      renders normally — that's fine, drafts are index-blocked by the
//      draft flag below via useSeo's noindex option (see below).

const NotFound: React.FC<{ slug: string | undefined }> = ({ slug }) => {
  useSeo({
    title: "Topic not found | Parcely",
    description: "This learn topic does not exist.",
    noindex: true,
  });
  return (
    <>
      <PublicPageHeader />
      <div className="legal-page" id="main" tabIndex={-1}>
        <div className="legal-content">
          <h1>Topic not found</h1>
          <p>
            We don't have a learn page for <code>{slug ?? ""}</code>. See the
            full list at <Link to="/learn">/learn</Link>.
          </p>
        </div>
      </div>
      <PublicPageFooter />
    </>
  );
};

const DraftBanner: React.FC = () => (
  <div
    role="status"
    style={{
      background: "rgba(180, 83, 9, 0.08)",
      borderLeft: "4px solid #b45309",
      padding: "10px 14px",
      margin: "0 0 20px",
      borderRadius: "0 8px 8px 0",
      fontSize: 14,
    }}
  >
    <strong>Draft.</strong> This page is a first pass under review before
    publish. Content will fill in shortly.
  </div>
);

const LearnTopicPage: React.FC = () => {
  const { propertyType } = useParams<{ propertyType: string }>();
  const topic = propertyType ? LEARN_TOPICS[propertyType] : undefined;

  // useSeo has to run at top level even on the not-found path; conditional
  // hook calls would violate React's rules. We compute a "noindex if draft
  // or missing" value and pass it, but only if the topic exists — otherwise
  // NotFound handles its own SEO.
  useSeo({
    title: topic?.seoTitle ?? "",
    description: topic?.seoDescription ?? "",
    // Draft topics MUST NOT be indexed. That's the whole point of shipping
    // stubs — they render for reviewers, not for search.
    noindex: topic?.draft ?? false,
  });

  if (!topic) return <NotFound slug={propertyType} />;

  // For non-draft topics, ArticlePage handles chrome + disclaimer. For drafts
  // we need to inject the DraftBanner ABOVE the body, so we render the same
  // layout inline rather than delegate to ArticlePage. Keeping the two paths
  // near-identical minimizes drift risk.
  if (topic.draft) {
    return (
      <>
        <PublicPageHeader />
        <div className="legal-page" id="main" tabIndex={-1}>
          <div className="legal-content">
            <DraftBanner />
            {topic.body}
            <hr className="legal-divider" />
            <p>
              <strong>This is general information, not legal or financial advice.</strong>{" "}
              North Carolina rules change and every case has its own facts.
              Confirm anything you plan to act on with the county tax office,
              the Clerk of Superior Court, and your own attorney before you
              bid.
            </p>
            <p className="legal-entity">
              <strong>Parcely LLC</strong>
              <br />
              Charlotte, NC
            </p>
          </div>
        </div>
        <PublicPageFooter />
      </>
    );
  }

  return <ArticlePage article={articleFromTopic(topic)} />;
};

export default LearnTopicPage;
